/**
 * Shared API client utilities
 *
 * Provides authentication headers and standardized fetch wrappers
 * (JSON + multipart) for the shipments UI plugin.
 *
 * The wrappers throw {@link ApiError} on failure and return the unwrapped
 * `data` payload directly, so callers don't repeat success/error unwrapping.
 * Paths are RELATIVE (`/api/...`) so they work behind the Vite dev proxy.
 */

import {
  ApiError,
  getApiHeaders,
  handleUnauthorized,
  type StandardApiResponse,
} from '@lognai/plugin-sdk';
import { buildQueryString, buildPaginationQuery } from '@lognai/frontend-core';

export { buildQueryString, buildPaginationQuery };
export type { ListQueryParams } from '@lognai/frontend-core';
export type { PaginationParams } from '@lognai/plugin-sdk';

/**
 * Standardized fetch wrapper with error handling.
 */
export const apiFetch = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('Unauthorized', { type: 'unauthorized', status: 401 });
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: response.statusText,
    }));
    throw new ApiError(error.error || error.message || `HTTP ${response.status}`, { ...error.details, status: response.status });
  }

  // A 204 carries no body by definition, and `response.json()` on an empty one
  // throws "Unexpected end of JSON input" — which surfaced a SUCCESSFUL delete
  // as a red error toast (QA ledger PROD-001). Every plugin's client had the
  // same unguarded call.
  //
  // Deliberately narrow: only the statuses that are DEFINED to have no body are
  // special-cased. Reading every response as text first would also cover a 200
  // with an empty body, but that is speculative, and it changes the parse path
  // for every call in the product to fix a case nobody has hit.
  if (response.status === 204 || response.status === 205) return undefined as T;

  const apiResponse: StandardApiResponse<T> = await response.json();

  if (!apiResponse.success) {
    throw new ApiError(apiResponse.error || 'API request failed', { ...(apiResponse as any).details, status: response.status });
  }

  return apiResponse.data;
};

/**
 * Multipart/form-data fetch wrapper.
 *
 * Uses the same auth headers as {@link apiFetch} but DELETES Content-Type so the
 * browser sets the multipart boundary itself — otherwise the server's JSON body
 * parser swallows the request and the upload fails.
 */
export const apiFetchMultipart = async <T>(
  url: string,
  formData: FormData,
  options: RequestInit = {}
): Promise<T> => {
  const headers = {
    ...getApiHeaders(),
    ...(options.headers as Record<string, string> | undefined),
  } as Record<string, string>;
  delete headers['Content-Type'];

  const response = await fetch(url, {
    method: 'POST',
    ...options,
    headers,
    body: formData,
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('Unauthorized', { type: 'unauthorized', status: 401 });
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: response.statusText,
    }));
    throw new ApiError(error.error || error.message || `HTTP ${response.status}`, { ...error.details, status: response.status });
  }

  if (response.status === 204 || response.status === 205) return undefined as T;

  const apiResponse: StandardApiResponse<T> = await response.json();

  if (!apiResponse.success) {
    throw new ApiError(apiResponse.error || 'API request failed', { ...(apiResponse as any).details, status: response.status });
  }

  return apiResponse.data;
};

export interface DownloadResult {
  /** Rows written, from `X-Export-Row-Count`, when the server reports it. */
  rowCount?: number;
  /** True when the server hit its export cap (`X-Export-Truncated`). */
  truncated: boolean;
}

/**
 * Fetch a file and trigger a browser download. Checks `res.ok` first, so an
 * error response is surfaced as an {@link ApiError} rather than silently saved
 * as a corrupt file. The filename comes from `Content-Disposition` when present.
 */
export const downloadFile = async (url: string, fallbackFilename: string): Promise<DownloadResult> => {
  const response = await fetch(url, { headers: getApiHeaders() });

  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('Unauthorized', { type: 'unauthorized', status: 401 });
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new ApiError(error.error || error.message || `Download failed (HTTP ${response.status})`, {
      ...error.details,
      status: response.status,
    });
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? fallbackFilename;
  const objectUrl = URL.createObjectURL(await response.blob());
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);

  const rowCount = Number(response.headers.get('X-Export-Row-Count'));
  return {
    rowCount: Number.isFinite(rowCount) && response.headers.has('X-Export-Row-Count') ? rowCount : undefined,
    truncated: response.headers.get('X-Export-Truncated') === 'true',
  };
};
